import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Hammer, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { fmt, errText } from "@/lib/api";

export default function Karigars() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });

  const { data: karigars, isLoading } = useQuery({ queryKey: ["karigars"], queryFn: async () => (await api.get("/karigars")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/karigars", form)).data,
    onSuccess: () => { toast.success("Karigar added"); qc.invalidateQueries({ queryKey: ["karigars"] }); setOpen(false); setForm({ name: "", phone: "" }); },
    onError: (e) => toast.error(errText(e)),
  });

  const update = useMutation({
    mutationFn: async () => (await api.put(`/karigars/${editing}`, form)).data,
    onSuccess: () => {
      toast.success("Karigar updated");
      qc.invalidateQueries({ queryKey: ["karigars"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setOpen(false); setEditing(null); setForm({ name: "", phone: "" });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const openEdit = (e, k) => {
    e.stopPropagation();
    setEditing(k.id);
    setForm({ name: k.name || "", phone: k.phone || "" });
    setOpen(true);
  };

  const totals = karigars?.reduce((a, k) => ({ work: a.work + k.total_work, paid: a.paid + k.paid, pending: a.pending + k.pending, active: a.active + k.active_orders }), { work: 0, paid: 0, pending: 0, active: 0 });

  return (
    <div className="space-y-6" data-testid="karigars-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Karigars</h1>
          <p className="text-sm text-muted-foreground mt-1">Work assignments & payment ledgers</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ name: "", phone: "" }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-karigar-button" className="rounded-full active:scale-95 transition-transform">
              <Plus className="h-4 w-4 mr-1.5" /> Add Karigar
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Karigar" : "New Karigar"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name *</Label>
                <Input data-testid="karigar-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                <Input data-testid="karigar-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <Button data-testid="karigar-save-button" className="w-full rounded-lg" disabled={!form.name.trim() || create.isPending || update.isPending} onClick={() => (editing ? update.mutate() : create.mutate())}>
                {editing ? "Save Changes" : "Save Karigar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {totals && (
        <div data-testid="all-karigar-orders-card" className="rounded-xl border bg-card p-5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">All Karigar Orders</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Active Items", totals.active], ["Total Work", fmt(totals.work)], ["Paid", fmt(totals.paid)], ["Pending", fmt(totals.pending)]].map(([l, v]) => (
              <div key={l}>
                <p className="font-heading text-xl font-semibold">{v}</p>
                <p className="text-xs text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-36 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {karigars?.map((k) => (
            <button
              key={k.id}
              data-testid={`karigar-card-${k.name.toLowerCase()}`}
              onClick={() => navigate(`/karigars/${k.id}`)}
              className="text-left rounded-xl border bg-card p-6 hover:border-foreground/20 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Hammer className="h-4 w-4 text-amber-500" />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-semibold">{k.name}</p>
                  <p className="text-xs text-muted-foreground">{k.active_orders} active item{k.active_orders !== 1 && "s"}</p>
                </div>
                <span role="button" tabIndex={0} title="Edit" data-testid={`edit-karigar-${k.id}`} onClick={(e) => openEdit(e, k)}
                  className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                  <Pencil className="h-3.5 w-3.5" />
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3 text-sm mb-4">
                {[
                  ["Current Work", `${k.current_pieces || 0} Pieces`],
                  ["Completed", `${k.completed_pieces || 0} Pieces`],
                  ["Pending", `${k.pending_pieces || 0} Pieces`],
                  ["Late", `${k.late_pieces || 0} Pieces`, k.late_pieces > 0 ? "text-red-500" : ""],
                ].map(([l, v, cls]) => (
                  <div key={l}><p className="text-xs text-muted-foreground">{l}</p><p className={`font-medium ${cls || ""}`}>{v}</p></div>
                ))}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3 text-sm border-t pt-3">
                <div><p className="text-xs text-muted-foreground">Total Work</p><p className="font-medium">{fmt(k.total_work)}</p></div>
                <div><p className="text-xs text-muted-foreground">Paid</p><p className="font-medium text-green-600">{fmt(k.paid)}</p></div>
                <div><p className="text-xs text-muted-foreground">Pending</p><p className={`font-medium ${k.pending > 0 ? "text-red-500" : ""}`}>{fmt(k.pending)}</p></div>
                <div><p className="text-xs text-muted-foreground">Today's Work</p><p className="font-medium text-amber-500">{k.todays_pieces || 0} Pieces</p></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
